import { FunctionComponent, useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { Colors } from 'src/utils/color';
import { getPatients, bulkAssignPatient } from 'src/services/api';
import { stripRedundantPatientPrefix } from 'src/utils/patientLabel';

const Overlay = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.4);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
`;

const Modal = styled.div`
    background: #fff;
    border-radius: 12px;
    padding: 24px;
    width: 440px;
    max-height: 80vh;
    display: flex;
    flex-direction: column;
    gap: 16px;
    box-shadow: 0 8px 32px rgba(0,0,0,0.15);
`;

const Title = styled.div`
    font-size: 16px;
    font-weight: 600;
    color: ${Colors.extra.black};
`;

const SearchInput = styled.input`
    width: 100%;
    padding: 10px 12px;
    border: 1px solid ${Colors.extra.light_gray};
    border-radius: 8px;
    font-size: 13px;
    outline: none;
    &:focus { border-color: ${Colors.theme.primary}; }
`;

const PatientList = styled.div`
    max-height: 200px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

const PatientOption = styled.button<{ $selected: boolean }>`
    padding: 10px 12px;
    border: none;
    border-radius: 6px;
    text-align: left;
    cursor: pointer;
    font-size: 13px;
    background: ${p => p.$selected ? `${Colors.theme.primary}15` : 'transparent'};
    color: ${p => p.$selected ? Colors.theme.primary : Colors.extra.black};
    font-weight: ${p => p.$selected ? 600 : 400};
    &:hover { background: ${Colors.extra.light_gray}44; }
`;

const Divider = styled.div`
    text-align: center;
    font-size: 12px;
    color: ${Colors.extra.dark_gray};
    padding: 4px 0;
`;

const Label = styled.div`
    font-size: 12px;
    color: ${Colors.extra.dark_gray};
    margin-bottom: 4px;
`;

const Hint = styled.div`
    font-size: 11px;
    color: ${Colors.extra.dark_gray};
    margin-top: 4px;
`;

const ButtonRow = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 8px;
`;

const Btn = styled.button<{ $primary?: boolean }>`
    padding: 8px 20px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    border: 1px solid ${p => p.$primary ? Colors.theme.primary : Colors.extra.light_gray};
    background: ${p => p.$primary ? Colors.theme.primary : '#fff'};
    color: ${p => p.$primary ? '#fff' : Colors.extra.dark_gray};
    &:hover { opacity: 0.9; }
    &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const ConfirmBox = styled.div`
    padding: 12px;
    border-radius: 8px;
    background: #FEF3C7;
    color: #92400E;
    font-size: 13px;
    line-height: 1.45;
`;

const ReassignContext = styled.div`
    padding: 12px;
    border-radius: 8px;
    background: #F3F4F6;
    font-size: 13px;
    color: ${Colors.extra.black};
    line-height: 1.5;
`;

const SuccessBox = styled.div`
    padding: 12px;
    border-radius: 8px;
    background: #D1FAE5;
    color: #065F46;
    font-size: 13px;
`;

const ErrorBox = styled.div`
    padding: 12px;
    border-radius: 8px;
    background: #FEE2E2;
    color: #DC2626;
    font-size: 13px;
`;

export type AssignPatientMode = 'link' | 'reassign';

export interface AssignPatientModalProps {
    jobIds: string[];
    mode: AssignPatientMode;
    /** Bare API label for the current patient (single-scan reassign). */
    currentPatientLabel?: string | null;
    currentPatientIsSession?: boolean | null;
    /** Override for Current line (e.g. bulk: "4 scans linked to subjects"). */
    currentSubjectSummary?: string;
    onClose: () => void;
    onAssigned?: () => void;
}

function displayWithPrefix(isSession: boolean | null | undefined, bareLabel: string): string {
    if (isSession === true) return `Session: ${bareLabel}`;
    return `Subject: ${bareLabel}`;
}

const AssignPatientModal: FunctionComponent<AssignPatientModalProps> = ({
    jobIds,
    mode,
    currentPatientLabel,
    currentPatientIsSession,
    currentSubjectSummary,
    onClose,
    onAssigned,
}) => {
    const [search, setSearch] = useState('');
    const [patients, setPatients] = useState<any[]>([]);
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [newSubjectId, setNewSubjectId] = useState('');
    const [modalMode, setModalMode] = useState<'select' | 'confirm' | 'assigning' | 'done' | 'error'>('select');
    const [resultMsg, setResultMsg] = useState('');
    const [errorMsg, setErrorMsg] = useState('');

    useEffect(() => {
        (async () => {
            try {
                const res = await getPatients(1, 50, search || undefined, true);
                setPatients(res?.data?.patients ?? []);
            } catch { /* */ }
        })();
    }, [search]);

    const selectedPatient = patients.find(p => p.id === selectedId);
    const newBare = newSubjectId.trim().toLowerCase();
    const newDisplay =
        selectedPatient != null
            ? displayWithPrefix(!!selectedPatient.is_session, selectedPatient.display_name || selectedPatient.external_id || '')
            : newBare
                ? `Subject: ${newBare}`
                : '—';

    const currentLine =
        currentSubjectSummary
        ?? (mode === 'reassign' && currentPatientLabel
            ? displayWithPrefix(currentPatientIsSession ?? false, currentPatientLabel)
            : null);

    const canAssign = selectedId || newSubjectId.trim();

    const handleConfirm = useCallback(async () => {
        setModalMode('assigning');
        try {
            const res = await bulkAssignPatient(
                jobIds,
                selectedId ?? undefined,
                selectedId ? undefined : newSubjectId.trim(),
                { forceReassign: mode === 'reassign' },
            );
            setResultMsg(`Assigned ${res.assigned} scan${res.assigned !== 1 ? 's' : ''} to ${res.patient_name}`);
            setModalMode('done');
            onAssigned?.();
        } catch (e: any) {
            setErrorMsg(e?.data?.error_message || e?.message || 'Assignment failed');
            setModalMode('error');
        }
    }, [jobIds, selectedId, newSubjectId, onAssigned, mode]);

    const title =
        mode === 'reassign'
            ? jobIds.length > 1
                ? 'Reassign scans'
                : 'Reassign scan'
            : 'Assign to Patient';

    return (
        <Overlay onClick={onClose}>
            <Modal onClick={e => e.stopPropagation()}>
                <Title>{title}</Title>

                {mode === 'reassign' && modalMode === 'select' && (currentLine || currentSubjectSummary) && (
                    <ReassignContext>
                        {currentSubjectSummary ? (
                            <>
                                <div>{currentSubjectSummary}</div>
                                <div style={{ marginTop: 8 }}>
                                    Reassigning will move {jobIds.length > 1 ? 'them' : 'it'} to a different patient record.
                                </div>
                            </>
                        ) : (
                            <>
                                <div>
                                    This scan is already linked to{' '}
                                    <strong>{currentLine}</strong>.
                                </div>
                                <div style={{ marginTop: 8 }}>
                                    Reassigning will move it to a different patient record.
                                </div>
                            </>
                        )}
                    </ReassignContext>
                )}

                {modalMode === 'select' && (
                    <>
                        <SearchInput
                            placeholder="Search by subject ID or name…"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            autoFocus
                        />
                        <PatientList>
                            {patients.map(p => (
                                <PatientOption
                                    key={p.id}
                                    $selected={selectedId === p.id}
                                    onClick={() => { setSelectedId(p.id); setNewSubjectId(''); }}
                                >
                                    {p.is_session ? 'Session' : 'Subject'}:{' '}
                                    {stripRedundantPatientPrefix(p.display_name || p.external_id || '', p.is_session)}
                                </PatientOption>
                            ))}
                            {patients.length === 0 && (
                                <Hint>No patients found. Create a new one below.</Hint>
                            )}
                        </PatientList>
                        <Divider>— or create new —</Divider>
                        <div>
                            <Label>New Subject ID</Label>
                            <SearchInput
                                placeholder="e.g. CLT123"
                                value={newSubjectId}
                                onChange={e => { setNewSubjectId(e.target.value); setSelectedId(null); }}
                            />
                        </div>
                        <ButtonRow>
                            <Btn onClick={onClose}>Cancel</Btn>
                            <Btn $primary disabled={!canAssign} onClick={() => setModalMode('confirm')}>
                                {mode === 'reassign' ? 'Continue' : 'Assign'}
                            </Btn>
                        </ButtonRow>
                    </>
                )}

                {modalMode === 'confirm' && (
                    <>
                        {mode === 'reassign' && (currentLine || currentSubjectSummary) && (
                            <ReassignContext>
                                <div><strong>Current:</strong> {currentSubjectSummary || currentLine}</div>
                                <div style={{ marginTop: 6 }}><strong>New:</strong> {newDisplay}</div>
                            </ReassignContext>
                        )}
                        <ConfirmBox>
                            Assign {jobIds.length} scan{jobIds.length !== 1 ? 's' : ''} to {selectedId ? 'Subject' : 'new subject'}
                            {selectedId ? `: ${selectedPatient?.display_name || selectedPatient?.external_id || ''}` : `: ${newBare}`}?
                            <br />
                            This cannot be undone from the UI.
                        </ConfirmBox>
                        <ButtonRow>
                            <Btn onClick={() => setModalMode('select')}>Back</Btn>
                            <Btn $primary onClick={handleConfirm}>Confirm</Btn>
                        </ButtonRow>
                    </>
                )}

                {modalMode === 'assigning' && <Hint>Assigning…</Hint>}

                {modalMode === 'done' && (
                    <>
                        <SuccessBox>{resultMsg}</SuccessBox>
                        <ButtonRow><Btn $primary onClick={onClose}>Done</Btn></ButtonRow>
                    </>
                )}

                {modalMode === 'error' && (
                    <>
                        <ErrorBox>{errorMsg}</ErrorBox>
                        <ButtonRow>
                            <Btn onClick={() => setModalMode('select')}>Back</Btn>
                            <Btn onClick={onClose}>Close</Btn>
                        </ButtonRow>
                    </>
                )}
            </Modal>
        </Overlay>
    );
};

export default AssignPatientModal;
